// Embed-only replacement for the `truncate-html` package.
//
// The real package pulls in cheerio + htmlparser2 (~150 KB minified) just to
// clip a string at N characters or words while preserving the surrounding
// HTML. In a browser-only bundle we can lean on the live DOM for the same
// job: parse the input into a detached <div>, walk text nodes counting
// chars/words, snip the last one when the budget is exhausted, and remove
// everything after. innerHTML then re-serialises with all tags closed.
//
// Surface matches the one place ReadMoreReadLess.jsx calls into:
//   truncate.setup({ byWords, ellipsis })
//   truncate(text, length)

const DEFAULTS = {
  byWords: false,
  ellipsis: '...',
  stripTags: false,
};

let globalOptions = { ...DEFAULTS };

function plainTextFallback(html, limit, opts) {
  const text = String(html).replace(/<[^>]*>/g, '');

  if (opts.byWords) {
    const words = text.split(/\s+/).filter(Boolean);
    if (words.length <= limit) {
      return html;
    }
    return words.slice(0, limit).join(' ') + opts.ellipsis;
  }

  if (text.length <= limit) {
    return html;
  }
  return text.slice(0, limit) + opts.ellipsis;
}

function truncate(html, length, options) {
  const opts = { ...DEFAULTS, ...globalOptions, ...(options || {}) };
  const limit = Number(length);

  if (typeof html !== 'string' || html.length === 0) {
    return html;
  }

  if (!Number.isFinite(limit) || limit <= 0) {
    return '';
  }

  // SSR or non-DOM environment - fall back to a plain-text clip so the embed
  // still renders something readable.
  if (typeof document === 'undefined') {
    return plainTextFallback(html, limit, opts);
  }

  const container = document.createElement('div');
  container.innerHTML = html;

  let remaining = limit;
  let truncated = false;

  const walk = (node) => {
    if (truncated) {
      // Anything past the truncation point is discarded so the rendered
      // collapsed-state matches what truncate-html would emit.
      node.parentNode && node.parentNode.removeChild(node);
      return;
    }

    if (node.nodeType === 3) {
      const text = node.nodeValue || '';

      if (opts.byWords) {
        // \S+\s* captures word-plus-trailing-space, preserving the original
        // spacing so "Hello world" stays "Hello world" instead of "Helloworld".
        const tokens = text.match(/\S+\s*/g) || [];

        if (tokens.length <= remaining) {
          remaining -= tokens.length;
          return;
        }

        const kept = tokens.slice(0, remaining).join('').replace(/\s+$/, '');
        node.nodeValue = kept + opts.ellipsis;
        remaining = 0;
        truncated = true;
        return;
      }

      if (text.length <= remaining) {
        remaining -= text.length;
        return;
      }

      node.nodeValue = text.slice(0, remaining) + opts.ellipsis;
      remaining = 0;
      truncated = true;
      return;
    }

    if (node.nodeType === 1) {
      const children = Array.prototype.slice.call(node.childNodes);
      children.forEach(walk);
    }
  };

  Array.prototype.slice.call(container.childNodes).forEach(walk);

  return container.innerHTML;
}

truncate.setup = function setup(options) {
  globalOptions = { ...globalOptions, ...(options || {}) };
};

export default truncate;
